import { useState, useEffect, useCallback } from "react";
import { Plus, Pencil, Trash2, Upload, Eye, MessageSquare, CalendarDays, Check, XCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  title: "", description: "", property_type: "villa", price: "",
  price_unit: "Cr", location: "", area: "", area_unit: "sq.ft",
  bedrooms: "", bathrooms: "", images: "", features: "", is_featured: false,
};

export default function AdminPage() {
  const { getAuthHeaders, logout } = useAuth();
  const [properties, setProperties] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  const authHeaders = getAuthHeaders();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [propRes, contRes, bookRes] = await Promise.all([
        axios.get(`${API}/properties`, { params: { per_page: 50 } }),
        axios.get(`${API}/contact`, { headers }),
        axios.get(`${API}/bookings`, { headers }),
      ]);
      setProperties(propRes.data.properties || []);
      setInquiries(contRes.data.inquiries || []);
      setBookings(bookRes.data.bookings || []);
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error("Session expired. Please login again.");
        logout();
      }
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, logout]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (prop) => {
    setEditingId(prop.id);
    setForm({
      title: prop.title || "",
      description: prop.description || "",
      property_type: prop.property_type || "villa",
      price: prop.price?.toString() || "",
      price_unit: prop.price_unit || "Cr",
      location: prop.location || "",
      area: prop.area || "",
      area_unit: prop.area_unit || "sq.ft",
      bedrooms: prop.bedrooms?.toString() || "",
      bathrooms: prop.bathrooms?.toString() || "",
      images: (prop.images || []).join("\n"),
      features: (prop.features || []).join(", "),
      is_featured: prop.is_featured || false,
    });
    setDialogOpen(true);
  };

  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploading(true);
    const uploadedUrls = [];
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        const res = await axios.post(`${API}/upload`, formData, {
          headers: { ...authHeaders, "Content-Type": "multipart/form-data" },
        });
        uploadedUrls.push(`${process.env.REACT_APP_BACKEND_URL}${res.data.url}`);
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    if (uploadedUrls.length > 0) {
      setForm((p) => ({
        ...p,
        images: p.images ? `${p.images}\n${uploadedUrls.join("\n")}` : uploadedUrls.join("\n"),
      }));
      toast.success(`${uploadedUrls.length} image(s) uploaded`);
    }
    setUploading(false);
    e.target.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.location || !form.price) {
      toast.error("Title, location, and price are required");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        title: form.title,
        description: form.description,
        property_type: form.property_type,
        price: parseFloat(form.price),
        price_unit: form.price_unit,
        location: form.location,
        area: form.area,
        area_unit: form.area_unit,
        bedrooms: form.bedrooms ? parseInt(form.bedrooms) : null,
        bathrooms: form.bathrooms ? parseInt(form.bathrooms) : null,
        images: form.images ? form.images.split("\n").map(s => s.trim()).filter(Boolean) : [],
        features: form.features ? form.features.split(",").map(s => s.trim()).filter(Boolean) : [],
        is_featured: form.is_featured,
      };

      if (editingId) {
        await axios.put(`${API}/properties/${editingId}`, payload, { headers: authHeaders });
        toast.success("Property updated");
      } else {
        await axios.post(`${API}/properties`, payload, { headers: authHeaders });
        toast.success("Property created");
      }
      setDialogOpen(false);
      fetchData();
    } catch (e) {
      toast.error("Failed to save property");
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this property?")) return;
    try {
      await axios.delete(`${API}/properties/${id}`, { headers: authHeaders });
      toast.success("Property deleted");
      fetchData();
    } catch (e) {
      toast.error("Failed to delete property");
    }
  };

  const handleBookingStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API}/bookings/${bookingId}?status=${status}`, {}, { headers: authHeaders });
      toast.success(`Booking ${status}`);
      fetchData();
    } catch (e) {
      toast.error("Failed to update booking");
    }
  };

  const handleDeleteBooking = async (id) => {
    if (!window.confirm("Delete this booking?")) return;
    try {
      await axios.delete(`${API}/bookings/${id}`, { headers: authHeaders });
      toast.success("Booking deleted");
      fetchData();
    } catch (e) {
      toast.error("Failed to delete booking");
    }
  };

  const typeLabels = { villa: "Villa", flat: "Apartment", land: "Land" };
  const statusColors = {
    pending: "text-yellow-400 border-yellow-400/30",
    confirmed: "text-green-400 border-green-400/30",
    cancelled: "text-red-400 border-red-400/30",
  };

  return (
    <div data-testid="admin-page" className="pt-24 pb-16 min-h-screen px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <p className="text-xs tracking-[0.2em] uppercase text-gold font-bold mb-2">Dashboard</p>
          <h1 className="font-heading text-2xl sm:text-3xl tracking-tight text-beige">
            Admin Panel
          </h1>
        </div>

        <Tabs defaultValue="properties" className="w-full">
          <TabsList className="bg-navy-light border border-white/10 rounded-sm mb-6">
            <TabsTrigger
              value="properties"
              data-testid="admin-tab-properties"
              className="data-[state=active]:bg-gold data-[state=active]:text-navy text-beige/60 rounded-sm text-sm"
            >
              <Eye size={14} className="mr-1.5" /> Properties ({properties.length})
            </TabsTrigger>
            <TabsTrigger
              value="inquiries"
              data-testid="admin-tab-inquiries"
              className="data-[state=active]:bg-gold data-[state=active]:text-navy text-beige/60 rounded-sm text-sm"
            >
              <MessageSquare size={14} className="mr-1.5" /> Inquiries ({inquiries.length})
            </TabsTrigger>
            <TabsTrigger
              value="bookings"
              data-testid="admin-tab-bookings"
              className="data-[state=active]:bg-gold data-[state=active]:text-navy text-beige/60 rounded-sm text-sm"
            >
              <CalendarDays size={14} className="mr-1.5" /> Bookings ({bookings.length})
            </TabsTrigger>
          </TabsList>

          {/* Properties Tab */}
          <TabsContent value="properties">
            <div className="flex justify-end mb-4">
              <button
                data-testid="admin-add-property-button"
                onClick={openCreate}
                className="flex items-center gap-2 bg-gold hover:bg-gold-hover text-navy font-bold text-xs tracking-widest uppercase px-5 py-2.5 transition-colors"
              >
                <Plus size={14} /> Add Property
              </button>
            </div>

            {loading ? (
              <div className="py-12 text-center text-beige/40">Loading...</div>
            ) : (
              <div className="border border-white/10 rounded-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/10 hover:bg-transparent">
                        <TableHead className="text-gold text-xs tracking-wider uppercase">Title</TableHead>
                        <TableHead className="text-gold text-xs tracking-wider uppercase">Type</TableHead>
                        <TableHead className="text-gold text-xs tracking-wider uppercase">Location</TableHead>
                        <TableHead className="text-gold text-xs tracking-wider uppercase">Price</TableHead>
                        <TableHead className="text-gold text-xs tracking-wider uppercase">Featured</TableHead>
                        <TableHead className="text-gold text-xs tracking-wider uppercase text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {properties.map((prop) => (
                        <TableRow key={prop.id} className="border-white/10 hover:bg-navy-light/50">
                          <TableCell className="text-beige text-sm font-medium max-w-[200px] truncate">{prop.title}</TableCell>
                          <TableCell className="text-beige/70 text-sm">{typeLabels[prop.property_type] || prop.property_type}</TableCell>
                          <TableCell className="text-beige/70 text-sm">{prop.location}</TableCell>
                          <TableCell className="text-gold text-sm">{"\u20B9"}{prop.price} {prop.price_unit}</TableCell>
                          <TableCell className="text-sm">
                            {prop.is_featured ? (
                              <span className="text-gold text-xs border border-gold/30 px-2 py-0.5">Featured</span>
                            ) : (
                              <span className="text-beige/30 text-xs">No</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                data-testid={`admin-edit-property-${prop.id}`}
                                onClick={() => openEdit(prop)}
                                className="text-beige/50 hover:text-gold transition-colors p-1.5"
                              >
                                <Pencil size={14} />
                              </button>
                              <button
                                data-testid="admin-property-delete-button"
                                onClick={() => handleDelete(prop.id)}
                                className="text-beige/50 hover:text-red-400 transition-colors p-1.5"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Inquiries Tab */}
          <TabsContent value="inquiries">
            {loading ? (
              <div className="py-12 text-center text-beige/40">Loading...</div>
            ) : inquiries.length === 0 ? (
              <div className="py-12 text-center text-beige/40">No inquiries yet</div>
            ) : (
              <div className="border border-white/10 rounded-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/10 hover:bg-transparent">
                        <TableHead className="text-gold text-xs tracking-wider uppercase">Name</TableHead>
                        <TableHead className="text-gold text-xs tracking-wider uppercase">Email</TableHead>
                        <TableHead className="text-gold text-xs tracking-wider uppercase">Phone</TableHead>
                        <TableHead className="text-gold text-xs tracking-wider uppercase">Property</TableHead>
                        <TableHead className="text-gold text-xs tracking-wider uppercase">Message</TableHead>
                        <TableHead className="text-gold text-xs tracking-wider uppercase">Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {inquiries.map((inq) => (
                        <TableRow key={inq.id} className="border-white/10 hover:bg-navy-light/50">
                          <TableCell className="text-beige text-sm">{inq.name}</TableCell>
                          <TableCell className="text-beige/70 text-sm">{inq.email}</TableCell>
                          <TableCell className="text-beige/70 text-sm">{inq.phone}</TableCell>
                          <TableCell className="text-beige/70 text-sm max-w-[150px] truncate">
                            {inq.property_title || "General"}
                          </TableCell>
                          <TableCell className="text-beige/70 text-sm max-w-[200px] truncate">{inq.message}</TableCell>
                          <TableCell className="text-beige/50 text-xs">
                            {new Date(inq.created_at).toLocaleDateString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            {loading ? (
              <div className="py-12 text-center text-beige/40">Loading...</div>
            ) : bookings.length === 0 ? (
              <div className="py-12 text-center text-beige/40">No site visit bookings yet</div>
            ) : (
              <div className="border border-white/10 rounded-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/10 hover:bg-transparent">
                        <TableHead className="text-gold text-xs tracking-wider uppercase">Name</TableHead>
                        <TableHead className="text-gold text-xs tracking-wider uppercase">Phone</TableHead>
                        <TableHead className="text-gold text-xs tracking-wider uppercase">Property</TableHead>
                        <TableHead className="text-gold text-xs tracking-wider uppercase">Date</TableHead>
                        <TableHead className="text-gold text-xs tracking-wider uppercase">Time</TableHead>
                        <TableHead className="text-gold text-xs tracking-wider uppercase">Status</TableHead>
                        <TableHead className="text-gold text-xs tracking-wider uppercase text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bookings.map((b) => (
                        <TableRow key={b.id} className="border-white/10 hover:bg-navy-light/50">
                          <TableCell className="text-beige text-sm">{b.name}</TableCell>
                          <TableCell className="text-beige/70 text-sm">{b.phone}</TableCell>
                          <TableCell className="text-beige/70 text-sm max-w-[150px] truncate">{b.property_title || "General"}</TableCell>
                          <TableCell className="text-beige/70 text-sm">{b.visit_date}</TableCell>
                          <TableCell className="text-beige/70 text-sm">{b.visit_time}</TableCell>
                          <TableCell>
                            <span className={`text-xs border px-2 py-0.5 capitalize ${statusColors[b.status] || "text-beige/50"}`}>
                              {b.status}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <button data-testid={`booking-status-trigger-${b.id}`} className="text-beige/50 hover:text-gold transition-colors p-1.5 text-xs border border-white/10 rounded-sm">
                                    Status
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent className="bg-navy-light border-white/10">
                                  <DropdownMenuItem
                                    onClick={() => handleBookingStatus(b.id, "confirmed")}
                                    className="text-green-400 focus:bg-navy focus:text-green-400 text-sm"
                                  >
                                    <Check size={12} className="mr-2" /> Confirm
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handleBookingStatus(b.id, "cancelled")}
                                    className="text-red-400 focus:bg-navy focus:text-red-400 text-sm"
                                  >
                                    <XCircle size={12} className="mr-2" /> Cancel
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handleBookingStatus(b.id, "pending")}
                                    className="text-yellow-400 focus:bg-navy focus:text-yellow-400 text-sm"
                                  >
                                    Pending
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                              <button
                                data-testid={`booking-delete-${b.id}`}
                                onClick={() => handleDeleteBooking(b.id)}
                                className="text-beige/50 hover:text-red-400 transition-colors p-1.5"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Property Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-navy-light border-white/10 text-beige max-w-2xl max-h-[90vh] overflow-y-auto rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-beige">
              {editingId ? "Edit Property" : "Add New Property"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="text-xs text-beige/50 mb-1 block">Title *</label>
                <Input
                  data-testid="admin-property-title-input"
                  value={form.title}
                  onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                  className="bg-navy border-white/10 text-beige rounded-sm h-10"
                  required
                />
              </div>

              <div>
                <label className="text-xs text-beige/50 mb-1 block">Type *</label>
                <Select value={form.property_type} onValueChange={(v) => setForm((p) => ({ ...p, property_type: v }))}>
                  <SelectTrigger data-testid="admin-property-type-select" className="bg-navy border-white/10 text-beige rounded-sm h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-navy-light border-white/10">
                    <SelectItem value="villa" className="text-beige hover:bg-navy focus:bg-navy focus:text-gold">Villa</SelectItem>
                    <SelectItem value="flat" className="text-beige hover:bg-navy focus:bg-navy focus:text-gold">Apartment</SelectItem>
                    <SelectItem value="land" className="text-beige hover:bg-navy focus:bg-navy focus:text-gold">Land</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs text-beige/50 mb-1 block">Location *</label>
                <Input
                  data-testid="admin-property-location-input"
                  value={form.location}
                  onChange={(e) => setForm((p) => ({ ...p, location: e.target.value }))}
                  className="bg-navy border-white/10 text-beige rounded-sm h-10"
                  required
                />
              </div>

              <div>
                <label className="text-xs text-beige/50 mb-1 block">Price *</label>
                <div className="flex gap-2">
                  <Input
                    data-testid="admin-property-price-input"
                    type="number"
                    step="0.1"
                    value={form.price}
                    onChange={(e) => setForm((p) => ({ ...p, price: e.target.value }))}
                    className="bg-navy border-white/10 text-beige rounded-sm h-10 flex-1"
                    required
                  />
                  <Select value={form.price_unit} onValueChange={(v) => setForm((p) => ({ ...p, price_unit: v }))}>
                    <SelectTrigger className="bg-navy border-white/10 text-beige rounded-sm h-10 w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-navy-light border-white/10">
                      <SelectItem value="Cr" className="text-beige focus:bg-navy focus:text-gold">Cr</SelectItem>
                      <SelectItem value="Lac" className="text-beige focus:bg-navy focus:text-gold">Lac</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="text-xs text-beige/50 mb-1 block">Area</label>
                <div className="flex gap-2">
                  <Input
                    data-testid="admin-property-area-input"
                    value={form.area}
                    onChange={(e) => setForm((p) => ({ ...p, area: e.target.value }))}
                    className="bg-navy border-white/10 text-beige rounded-sm h-10 flex-1"
                  />
                  <Select value={form.area_unit} onValueChange={(v) => setForm((p) => ({ ...p, area_unit: v }))}>
                    <SelectTrigger className="bg-navy border-white/10 text-beige rounded-sm h-10 w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-navy-light border-white/10">
                      <SelectItem value="sq.ft" className="text-beige focus:bg-navy focus:text-gold">sq.ft</SelectItem>
                      <SelectItem value="sq.m" className="text-beige focus:bg-navy focus:text-gold">sq.m</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="text-xs text-beige/50 mb-1 block">Bedrooms</label>
                <Input
                  data-testid="admin-property-bedrooms-input"
                  type="number"
                  value={form.bedrooms}
                  onChange={(e) => setForm((p) => ({ ...p, bedrooms: e.target.value }))}
                  className="bg-navy border-white/10 text-beige rounded-sm h-10"
                />
              </div>

              <div>
                <label className="text-xs text-beige/50 mb-1 block">Bathrooms</label>
                <Input
                  data-testid="admin-property-bathrooms-input"
                  type="number"
                  value={form.bathrooms}
                  onChange={(e) => setForm((p) => ({ ...p, bathrooms: e.target.value }))}
                  className="bg-navy border-white/10 text-beige rounded-sm h-10"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="text-xs text-beige/50 mb-1 block">Description</label>
                <Textarea
                  data-testid="admin-property-description-input"
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  className="bg-navy border-white/10 text-beige rounded-sm min-h-[80px]"
                />
              </div>

              {/* Image Upload */}
              <div className="sm:col-span-2">
                <label className="text-xs text-beige/50 mb-1 block">Images</label>
                <div className="flex gap-2 mb-2">
                  <label
                    data-testid="admin-image-upload-button"
                    className={`flex items-center gap-2 cursor-pointer bg-navy border border-gold/30 hover:border-gold text-gold text-xs tracking-wider uppercase px-4 py-2 transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`}
                  >
                    <Upload size={14} /> {uploading ? "Uploading..." : "Upload Images"}
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </label>
                </div>
                <Textarea
                  data-testid="admin-property-images-input"
                  value={form.images}
                  onChange={(e) => setForm((p) => ({ ...p, images: e.target.value }))}
                  placeholder="Image URLs (one per line) or use upload button above"
                  className="bg-navy border-white/10 text-beige rounded-sm min-h-[60px] placeholder:text-beige/20 text-xs"
                />
                {/* Image Previews */}
                {form.images && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {form.images.split("\n").filter(Boolean).map((url, i) => (
                      <div key={i} className="w-16 h-12 overflow-hidden border border-white/10 rounded-sm">
                        <img src={url.trim()} alt={`Preview ${i}`} className="w-full h-full object-cover" onError={(e) => e.target.style.display = "none"} />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="sm:col-span-2">
                <label className="text-xs text-beige/50 mb-1 block">Features (comma-separated)</label>
                <Input
                  data-testid="admin-property-features-input"
                  value={form.features}
                  onChange={(e) => setForm((p) => ({ ...p, features: e.target.value }))}
                  placeholder="Pool, Garden, Parking"
                  className="bg-navy border-white/10 text-beige rounded-sm h-10 placeholder:text-beige/20"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_featured"
                  data-testid="admin-property-featured-checkbox"
                  checked={form.is_featured}
                  onChange={(e) => setForm((p) => ({ ...p, is_featured: e.target.checked }))}
                  className="accent-gold"
                />
                <label htmlFor="is_featured" className="text-sm text-beige/70">Featured Property</label>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
              <button
                type="button"
                onClick={() => setDialogOpen(false)}
                className="text-beige/50 hover:text-beige text-sm px-4 py-2 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                data-testid="admin-property-submit-button"
                disabled={submitting}
                className="bg-gold hover:bg-gold-hover text-navy font-bold text-sm tracking-wider uppercase px-6 py-2.5 transition-colors disabled:opacity-50"
              >
                {submitting ? "Saving..." : editingId ? "Update" : "Create"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
